import { onBeforeUnmount, onMounted, watch, watchEffect, type Ref } from 'vue'

interface Options {
  /** Optional scroll root. Defaults to the viewport. */
  root?: Ref<HTMLElement | null>
  /** IntersectionObserver threshold. */
  threshold?: number
  /** Returns true when another page can be fetched (has next + not fetching). */
  canLoadMore: () => boolean
  /** Invoked when the sentinel enters view and `canLoadMore()` is true. */
  onLoadMore: () => void
}

/**
 * Wires an IntersectionObserver to a sentinel element. When the sentinel
 * scrolls into view, triggers `onLoadMore` — provided `canLoadMore()` is
 * true (the caller usually combines `hasNextPage` + `!isFetchingNextPage`).
 *
 * Handles the common ceremony: re-creating the observer when the sentinel
 * mounts/remounts, disconnecting on unmount. Works for window scroll (omit
 * `root`) and for inner-scroll containers (pass a root ref).
 *
 * Also handles the case where the sentinel is permanently visible inside a
 * non-overflowing root (e.g. the list is shorter than the viewport). In that
 * case the IntersectionObserver fires only once, so a watchEffect re-checks
 * after every reactive state change (loading → idle) and triggers the next
 * page load as long as the sentinel is still in view.
 */
export function useInfiniteScrollObserver(
  sentinelRef: Ref<HTMLElement | null>,
  options: Options,
): void {
  let observer: IntersectionObserver | null = null
  let sentinelInView = false

  const tryLoad = () => {
    if (sentinelInView && options.canLoadMore()) {
      options.onLoadMore()
    }
  }

  const setup = () => {
    observer?.disconnect()
    const sentinel = sentinelRef.value
    if (!sentinel) return
    observer = new IntersectionObserver(
      (entries) => {
        sentinelInView = entries[0]?.isIntersecting ?? false
        tryLoad()
      },
      {
        root: options.root?.value ?? null,
        threshold: options.threshold ?? 0,
      },
    )
    observer.observe(sentinel)
  }

  onMounted(setup)
  watch(sentinelRef, setup)
  onBeforeUnmount(() => observer?.disconnect())

  // Re-check whenever loading state flips back to idle while the sentinel is
  // still in the root's viewport. This covers non-overflowing lists where the
  // IntersectionObserver only fires once (intersection ratio never changes).
  watchEffect(tryLoad)
}
